import { AddressController } from '@adornis/address/client/address-controller.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import countries from '@chancenwerk/address/countries.json';
import { Adresse } from '@chancenwerk/db/adresse.js';
import '@chancenwerk/design/components/cr-dropdown-search-selection.js';
import '@chancenwerk/design/components/cr-input.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, switchMap, takeUntil } from 'rxjs';
import '../design/components/cr-flex.js';
import '../design/components/fonts.js';
import './chancen-component-zip-city-picker.js';

/**
 * @customElement chancen-component-address-picker
 *
 * **Possible properties:**
 * - `space : string` => defines the space between the inputs to adapt to different styles. Use this.spacing for more info
 */
@customElement('chancen-component-address-picker')
export class ChancenComponentAddressPicker extends FormField<Maybe<Adresse>> {
  @property({ attribute: false }) enabledFields = new RXController<null | BaseQLSelectionSet<Adresse>>(this, null);

  @property({ attribute: false }) public form = new FormController<Adresse>(this, this.value.observable, {
    enabledFields: this.enabledFields.observable,
  });
  @property({ attribute: 'space', type: String, reflect: true }) public space = 'md';
  @property({ attribute: 'show-care-of', type: Boolean, reflect: true }) public showCareOf = false;
  @property({ attribute: 'hide-all-countries', type: Boolean, reflect: true }) public hideAllCountries = false;
  @property({ attribute: true, type: Boolean, reflect: true }) public required = false;

  @state() private _addressController = new AddressController(this, {
    language: 'de',
    countries: [],
  });

  // @ts-expect-error _whenChanging state to update the dom
  @state() private readonly _changing = new RXController(
    this,
    this.value.observable.pipe(
      filter(Boolean),
      switchMap(value => value.whenChanging),
    ),
  );

  @state() private _allCountries = new RXController(this, false);

  override connectedCallback(): void {
    super.connectedCallback();

    // Ensure an empty adresse object
    this.value.observable.pipe(takeUntil(this.disconnected)).subscribe(value => {
      if (!value) {
        this.value.next(new Adresse({}));
      } else {
        if (value.country) {
          this._allCountries.next(true);
        }
      }
    });

    this._allCountries.observable.pipe(takeUntil(this.disconnected)).subscribe(allCountries => {
      this._addressController = new AddressController(this, {
        language: 'de',
        countries: allCountries ? [] : ['de'],
      });
    });
  }

  override render() {
    const showWarning = this.value.value?.strasse ? !/\d/.test(this.value.value.strasse) : false;
    const country = this.form.document?.country ? countries[this.form.document.country] : null;
    return html`
      <cr-flex space=${this.space} wrap>
        ${showWarning
          ? html` <cr-text ${css({ color: this.colors.secondary })}> Die Adresse enthält keine Hausnummer! </cr-text> `
          : nothing}
        ${!this.hideAllCountries
          ? html`<cr-flex horizontal space="sm">
              <cr-checkbox
                ${css({ marginBottom: this.spacing.sm })}
                label="ausländische Adresse"
                .value=${this._allCountries.value}
                @value-picked=${(e: ValueEvent<boolean>) => this._allCountries.next(e.detail.value)}
              ></cr-checkbox>
              <chancen-component-info-icon
                text="Bei Aktivierung der Checkbox sucht das System über ausländische Adressen."
              ></chancen-component-info-icon>
            </cr-flex>`
          : nothing}
        <cr-flex horizontal space=${this.space} wrap>
          <cr-dropdown-search-selection
            ?disabled=${this.disabled}
            clearable
            string-suggestion-only
            ${css({ minWidth: '200px' })}
            flex
            placeholder=${'Straße und Hausnummer'.concat(this.required ? ' *' : '')}
            ${this.form.field('strasse')}
            ${this._addressController.address()}
            @value-picked=${() => this._emitValuePicked()}
          ></cr-dropdown-search-selection>
          <slot name="care-of"></slot>
          ${this.showCareOf
            ? html`
                <cr-input
                  ?disabled=${this.disabled}
                  ${css({ minWidth: '200px' })}
                  flex
                  clearable
                  placeholder="Adresszusatz"
                  ${this.form.field('careOf')}
                  @value-picked=${() => this._emitValuePicked()}
                ></cr-input>
              `
            : nothing}
        </cr-flex>
        <chancen-component-zip-city-picker
          flex
          ?disabled=${this.disabled}
          ?required=${this.required}
          ?allCountries=${this._allCountries.value}
          .enabledFields=${this.enabledFields.value ? this.enabledFields.value.zipCity ?? {} : null}
          clearable
          space=${this.space}
          ${this.form.field('zipCity')}
          .addressController=${this._addressController}
          @value-picked=${() => this._emitValuePicked()}
        ></chancen-component-zip-city-picker>
        <cr-dropdown-search-selection
          ${css({ display: 'none' })}
          ${this._addressController.province()}
          @value-picked=${(e: ValueEvent<Maybe<string>>) => this._emitProvince(e.detail.value ?? '')}
        ></cr-dropdown-search-selection>
        ${this._allCountries.value
          ? html` <cr-dropdown-search-selection
              ?disabled=${this.disabled}
              clearable
              string-suggestion-only
              flex
              placeholder=${`Land ${country ? `(${country})` : ''}`}
              ${this.form.field('country')}
              ${this._addressController.country()}
              @value-picked=${() => this._emitValuePicked()}
            ></cr-dropdown-search-selection>`
          : nothing}
      </cr-flex>
    `;
  }

  private _emitProvince(province: string) {
    const event = new CustomEvent('province-picked', {
      detail: {
        value: province,
      },
    });
    this.dispatchEvent(event);
  }

  private _emitValuePicked() {
    const event = new CustomEvent('value-picked', {
      detail: {
        value: this.value.value,
      },
    });
    this.dispatchEvent(event);
  }
}
